import axios, { AxiosResponse } from 'axios';
import type {
  CreateInterviewRequest,
  CreateInterviewResponse,
  InterviewStatusRequest,
  InterviewStatusResponse,
  GetInterviewStatusResponse,
  GenerateInsightsRequest,
  GenerateInsightsResponse,
  PipecatConversationRequest,
  PipecatConversationResponse
} from '../types/interview';
import { INTERVIEW_API_URL, BASE_URL } from '../config/api';

// API URLs from environment variables
const PIPECAT_API_URL = import.meta.env.VITE_PIPECAT_API_URL || 'https://api.pipecat.daily.co';
const BACKEND_API_URL = INTERVIEW_API_URL;

class InterviewService {
  private baseUrl: string;
  private pipecatUrl: string;

  constructor() {
    this.baseUrl = BACKEND_API_URL;
    this.pipecatUrl = PIPECAT_API_URL;
  }

  /**
   * Create a new interview record in the backend
   */
  async createInterview(
    data: CreateInterviewRequest, 
    customApiUrl?: string
  ): Promise<CreateInterviewResponse> {
    try {
      const apiUrl = customApiUrl || this.baseUrl;
      const response: AxiosResponse<CreateInterviewResponse> = await axios.post(
        `${apiUrl}/interview/create`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to create interview record'
      );
    }
  }

  /**
   * Update interview status
   */
  async updateInterviewStatus(
    data: InterviewStatusRequest, 
    customApiUrl?: string
  ): Promise<InterviewStatusResponse> {
    try {
      const apiUrl = customApiUrl || this.baseUrl;
      const response: AxiosResponse<InterviewStatusResponse> = await axios.put(
        `${apiUrl}/interview/status`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to update interview status'
      );
    }
  }

  /**
   * Get current interview status
   */
  async getInterviewStatus(interviewId: string): Promise<GetInterviewStatusResponse> {
    try {
      const response: AxiosResponse<GetInterviewStatusResponse> = await axios.get(
        `${this.baseUrl}/interview/${interviewId}/status`,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to get interview status'
      );
    }
  }

  /**
   * Generate candidate insights from interview
   */
  async generateCandidateInsights(data: GenerateInsightsRequest): Promise<GenerateInsightsResponse> {
    try {
      const response: AxiosResponse<GenerateInsightsResponse> = await axios.post(
        `${this.baseUrl}/interview/insights/candidate`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to generate candidate insights'
      );
    }
  }

  /**
   * Generate practice interview insights
   */
  async generatePracticeInsights(data: GenerateInsightsRequest): Promise<GenerateInsightsResponse> {
    try {
      const response: AxiosResponse<GenerateInsightsResponse> = await axios.post(
        `${this.baseUrl}/interview/insights/practice`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to generate practice insights'
      );
    }
  }

  /**
   * Create Pipecat conversation
   */
  async createPipecatConversation(
    agentName: string,
    data: PipecatConversationRequest,
    authToken: string
  ): Promise<PipecatConversationResponse> {
    try {
      const requestUrl = `${this.pipecatUrl}/v1/public/${agentName}/start`;
      
      const response: AxiosResponse<PipecatConversationResponse> = await axios.post(
        requestUrl,
        data,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to create Pipecat conversation'
      );
    }
  }

  /**
   * End Pipecat conversation
   */
  async endPipecatConversation(
    conversationId: string,
    authToken: string
  ): Promise<{ success: boolean }> {
    try {
      const response: AxiosResponse<{ success: boolean }> = await axios.post(
        `${this.pipecatUrl}/v1/conversations/${conversationId}/end`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        'Failed to end Pipecat conversation'
      );
    }
  }
}

// Create and export a singleton instance
export const interviewService = new InterviewService();

// Export individual methods with proper binding to maintain context
export const createInterview = (data: CreateInterviewRequest, customApiUrl?: string) => 
  interviewService.createInterview(data, customApiUrl);

export const updateInterviewStatus = (data: InterviewStatusRequest, customApiUrl?: string) => 
  interviewService.updateInterviewStatus(data, customApiUrl);

export const getInterviewStatus = (interviewId: string) => 
  interviewService.getInterviewStatus(interviewId);

export const generateCandidateInsights = (data: GenerateInsightsRequest) => 
  interviewService.generateCandidateInsights(data);

export const generatePracticeInsights = (data: GenerateInsightsRequest) => 
  interviewService.generatePracticeInsights(data);

export const createPipecatConversation = (agentName: string, data: PipecatConversationRequest, authToken: string) => 
  interviewService.createPipecatConversation(agentName, data, authToken);

export const endPipecatConversation = (conversationId: string, authToken: string) => 
  interviewService.endPipecatConversation(conversationId, authToken);

// Default export
export default interviewService;
